import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { Rectangle, } from "pixi.js";
import { useEventListener } from "usehooks-ts";
import { chunkString } from "@poscon/shared-types";
import { eramFontNameMap, eramTextDimensionMap, getBitmapTextStyles } from "../../eramFont.js";
import { colorNameMap } from "../../colorNameMap.js";
import { usePixiMouseEventListener } from "../hooks/usePixiMouseEventListener.js";
import { useInputContext } from "../contexts/inputContext.js";
import { useOnMount } from "../hooks/useOnMount.js";
import { useFocused } from "../hooks/useFocused.js";
import { usePixiOnClickOutside } from "../hooks/usePixiOnClickOutside.js";
import { TBE } from "../../buttons.js";
import { useUiIsLocked } from "../hooks/useUiIsLocked";
import { useCustomEventListener } from "../hooks/useCustomEventListener";
import { useStableCallback } from "../hooks/useStableCallback";
import { errorTone } from "../../audio/errorTone";
import { syncUiInputAction } from "../../actions.js";
import { useDispatch } from "react-redux";
import { EramKeyboardEvent } from "../../eramKeyboardEvent.js";
const cursorOffsetMap = {
    1: 1,
    2: -1,
    3: -1,
    4: -1,
    5: -1,
};
export const EramInput = ({ value, onmousedown, cursorPosition, isActive, fontSize = 2, chunkSize = 0, x = 0, y = 0, padding = 0, textX = 1, textY = 1, width, height = eramTextDimensionMap[fontSize].height + 2, zIndex, label, borderTint, fill, tint = colorNameMap.lightGrey, highlightColor, disabled = false, }) => {
    const ref = useRef(null);
    const { setActiveInput } = useInputContext();
    const focused = useFocused(ref, !highlightColor);
    const fontDimension = eramTextDimensionMap[fontSize];
    usePixiMouseEventListener((event) => onmousedown?.(event), ref, true);
    usePixiOnClickOutside(ref, () => {
        if (isActive) {
            setActiveInput(null);
        }
    });
    const text = chunkSize > 0
        ? chunkString(value, chunkSize).join("\n")
        : value.slice(0, Math.floor(width / fontDimension.width));
    return (_jsx("container", { ref: ref, x: x, y: y - 1, hitArea: new Rectangle(0, 0, width + 2 * padding, height + 2 * padding), eventMode: disabled ? "none" : "static", label: label, sortableChildren: true, zIndex: zIndex ?? (focused ? 1 : 0), children: _jsxs("container", { x: padding, y: padding, eventMode: "static", children: [_jsx("graphics", { zIndex: 0, eventMode: "none", draw: (graphics) => {
                        graphics.clear();
                        graphics.rect(0, 0, width, height);
                        if (fill !== undefined) {
                            graphics.fill(fill);
                        }
                        if (focused && highlightColor) {
                            graphics.stroke({ width: 1, color: highlightColor, alignment: 0 });
                        }
                        else if (borderTint) {
                            graphics.stroke({ width: 1, color: borderTint, alignment: 0 });
                        }
                    } }), _jsx("graphics", { zIndex: 1, draw: (graphics) => {
                        graphics.clear();
                        if (isActive) {
                            const cursorX = textX +
                                Math.min(chunkSize > 0 ? cursorPosition % chunkSize : cursorPosition, Math.floor(width / fontDimension.width - 1)) *
                                    fontDimension.width;
                            const cursorY = textY -
                                1 +
                                cursorOffsetMap[fontSize] +
                                ((chunkSize > 0 ? Math.floor(cursorPosition / chunkSize) : 0) + 1) * fontDimension.height;
                            graphics.moveTo(cursorX, cursorY);
                            graphics.lineTo(cursorX + fontDimension.width, cursorY);
                            graphics.stroke({ width: 1, color: tint });
                        }
                    } }), _jsx("bitmapText", { zIndex: 2, text: text, x: textX, y: textY, eventMode: "none", style: { ...getBitmapTextStyles(eramFontNameMap[fontSize]), fill: tint } })] }) }));
};
export const useInputProps = (path, initialValue, onEnter, { resetOnMouseDown = false, maxLength, initialShowInput = true, initialCursorPos = initialValue.length, focusOnMount = true, pasteOnTBE = false, keyPredicate = () => true, onInvalidCharacterEntered, } = {}) => {
    const dispatch = useDispatch();
    const { activeInput, setActiveInput } = useInputContext();
    const [value, setValue] = useState(initialValue);
    const [cursorPosition, setCursorPosition] = useState(initialCursorPos);
    const [showInput, setShowInput] = useState(initialShowInput);
    const uiIsLocked = useUiIsLocked();
    const inputRef = useRef({ value, cursorPosition });
    const id = `input/${path}`;
    useOnMount(() => {
        if (focusOnMount) {
            setActiveInput(id);
        }
    });
    const _setInputValue = useCallback((value) => {
        inputRef.current.value = value;
        inputRef.current.cursorPosition = value.length;
        setValue(value);
        setCursorPosition(value.length);
    }, []);
    const isActive = activeInput === id;
    useEffect(() => {
        // void replayManager.recordReplayEvent("input", {
        //   type: "input",
        //   path,
        //   showInput,
        //   value,
        // });
        dispatch(syncUiInputAction({ path, value, cursorPosition, showInput }));
    }, [path, value, cursorPosition, dispatch, showInput]);
    useCustomEventListener("replay:input", (event) => {
        const { path: replayPath, value: replayValue, showInput } = event.detail;
        if (replayPath === path) {
            setShowInput(showInput);
            inputRef.current.value = replayValue;
            inputRef.current.cursorPosition = replayValue.length;
            setValue(replayValue);
            setCursorPosition(replayValue.length);
        }
    });
    useCustomEventListener("ui:sync:input", (event) => {
        const { path: syncPath, value: syncValue, showInput, cursorPosition } = event.detail;
        if (syncPath === path) {
            if (typeof showInput === "boolean") {
                setShowInput(showInput);
            }
            inputRef.current.value = syncValue;
            inputRef.current.cursorPosition = cursorPosition;
            setValue(syncValue);
            setCursorPosition(cursorPosition);
        }
    });
    const keydownHandler = (e) => {
        const event = new EramKeyboardEvent(e);
        event.nativeEvent.stopPropagation();
        const { cursorPosition, value } = inputRef.current;
        switch (event.key) {
            case "Enter":
                onEnter?.(value);
                break;
            case "Escape":
                setValue("");
                setCursorPosition(0);
                break;
            case "ArrowLeft":
                inputRef.current.cursorPosition = Math.max(0, cursorPosition - 1);
                break;
            case "ArrowRight":
                inputRef.current.cursorPosition = Math.min(value.length, cursorPosition + 1);
                break;
            case "Backspace":
                if (cursorPosition > 0) {
                    inputRef.current.value = value.slice(0, cursorPosition - 1).concat(value.slice(cursorPosition));
                    inputRef.current.cursorPosition = Math.max(0, cursorPosition - 1);
                }
                break;
            case "Delete":
                inputRef.current.value = value.slice(0, cursorPosition).concat(value.slice(cursorPosition + 1));
                break;
            default: {
                if (event.token === null) {
                    return;
                }
                const newValue = value.slice(0, cursorPosition) + event.token + value.slice(cursorPosition);
                if (keyPredicate(event.token, newValue) && (maxLength === undefined || value.length < maxLength)) {
                    inputRef.current.value = newValue;
                    inputRef.current.cursorPosition = cursorPosition + 1;
                }
                else {
                    errorTone.tryPlay();
                    onInvalidCharacterEntered?.(value, event.token);
                }
            }
        }
        setValue(inputRef.current.value);
        setCursorPosition(inputRef.current.cursorPosition);
    };
    useEventListener("keydown", (e) => {
        if (isActive && !uiIsLocked) {
            if (!showInput) {
                setShowInput(true);
            }
            keydownHandler(e);
        }
    }, undefined, true);
    const onmousedown = useStableCallback(async (event) => {
        setActiveInput(id);
        if (pasteOnTBE && event.button === TBE) {
            if (window.navigator?.clipboard) {
                const text = await window.navigator.clipboard.readText();
                inputRef.current.value = text.toUpperCase();
                inputRef.current.cursorPosition = text.length;
                setValue(text.toUpperCase());
                setCursorPosition(text.length);
            }
        }
        else if (resetOnMouseDown) {
            inputRef.current = { value: "", cursorPosition: 0 };
            setValue("");
            setCursorPosition(0);
        }
    });
    const setInputFocus = useStableCallback((value = true) => {
        setActiveInput((prev) => {
            if (value) {
                return id;
            }
            return prev === id ? null : prev;
        });
    });
    return {
        setInputFocus,
        value,
        isActive,
        setInputValue: _setInputValue,
        showInput,
        setShowInput,
        onmousedown,
        cursorPosition,
    };
};
//# sourceMappingURL=EramInput.js.map